import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Disc3, ExternalLink, Globe, Heart, Mic2, Search, Users } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/AppShell";
import { PAGE_CONTAINER } from "@/components/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getArtistBySlug, refreshArtistBySlug, type ArtistPage } from "@/lib/artists.functions";
import { isFollowingArtist, toggleArtistFollow } from "@/lib/artist-follows.functions";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { thumbUrl } from "@/lib/image-thumb";
import { buildAlbumParam } from "@/lib/slugify";



export const Route = createFileRoute("/artistas/$slug")({
  validateSearch: (search: Record<string, unknown>) => ({
    n: typeof search.n === "string" ? search.n.slice(0, 120) : undefined,
  }),
  loaderDeps: ({ search }) => ({ n: search.n }),
  loader: ({ params, deps }) => getArtistBySlug({ data: { slug: params.slug, name: deps.n } }),
  staleTime: 5 * 60_000,
  pendingMs: 0,
  pendingMinMs: 400,
  pendingComponent: ArtistPageLoading,
  head: ({ loaderData }) => {
    const a = loaderData as ArtistPage | null;
    const name = a?.name ?? "Artista";
    const title = `${name} — discografia em vinil | Viny Club`;
    const description = a?.bio
      ? a.bio.slice(0, 155)
      : `Discografia em vinil de ${name} e os discos mais presentes nas coleções da comunidade Viny.`;
    const image = a?.imageUrl && a.imageUrl.startsWith("https://") ? a.imageUrl : null;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "profile" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(image
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
      ],
    };
  },
  errorComponent: () => (
    <AppShell>
      <div className={PAGE_CONTAINER}>
        <p className="py-16 text-center text-sm text-muted-foreground">
          Não foi possível carregar este artista agora.
        </p>
      </div>
    </AppShell>
  ),
  notFoundComponent: () => (
    <AppShell>
      <div className={PAGE_CONTAINER}>
        <p className="py-16 text-center text-sm text-muted-foreground">Artista não encontrado.</p>
      </div>
    </AppShell>
  ),
  component: ArtistPageView,
});

function ArtistPageView() {
  const artist = Route.useLoaderData() as ArtistPage | null;
  const { slug } = Route.useParams();
  const { isAuthenticated } = useCurrentUser();
  const qc = useQueryClient();
  const router = useRouter();
  const [expanded, setExpanded] = useState(false);
  const refreshStarted = useRef(false);

  const isFollowingFn = useServerFn(isFollowingArtist);
  const toggleFn = useServerFn(toggleArtistFollow);
  const refreshFn = useServerFn(refreshArtistBySlug);

  useEffect(() => {
    if (!artist?.needsRefresh || refreshStarted.current) return;
    refreshStarted.current = true;
    void refreshFn({ data: { slug, name: artist.name } })
      .then((result) => {
        if (result.updated) void router.invalidate();
      })
      .catch(() => undefined);
  }, [artist?.name, artist?.needsRefresh, refreshFn, router, slug]);

  const following = useQuery({
    queryKey: ["artist-following", slug],
    queryFn: () => isFollowingFn({ data: { slug } }),
    enabled: isAuthenticated && !!artist,
    staleTime: 60_000,
  });

  const toggle = useMutation({
    mutationFn: () => toggleFn({ data: { slug } }),
    onSuccess: (res) => {
      qc.setQueryData(["artist-following", slug], res.following);
      qc.invalidateQueries({ queryKey: ["my-followed-artists"] });
      toast.success(res.following ? "Seguindo o artista" : "Deixou de seguir");
    },
    onError: () => toast.error("Não foi possível atualizar agora."),
  });

  if (!artist) {
    return (
      <AppShell>
        <div className={PAGE_CONTAINER}>
          <p className="py-16 text-center text-sm text-muted-foreground">
            Artista não encontrado.
          </p>
        </div>
      </AppShell>
    );
  }

  // Apenas o site oficial (quando existe) fica ao lado do botão de seguir.
  const links = [{ url: artist.websiteUrl, label: "Site oficial", Icon: Globe }].filter(
    (l) => !!l.url,
  );


  const bio = artist.bio ?? "";
  const shortBio = bio.length > 420 && !expanded ? `${bio.slice(0, 420)}…` : bio;

  return (
    <AppShell>
      {/* Cabeçalho no mesmo card escuro usado no resto do app */}
      <div className={`${PAGE_CONTAINER} mb-6 pt-4 lg:mb-8 lg:pt-8`}>
        <div className="relative isolate overflow-hidden rounded-[var(--radius-2xl)] bg-[#1A1A1A]">
          <div className="grid grid-cols-1 gap-6 p-6 py-8 lg:grid-cols-[auto_1fr] lg:items-center lg:p-10 lg:py-11">
            <span className="grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-full border-2 border-white/10 bg-white/[0.06] lg:h-32 lg:w-32">
              {artist.imageUrl ? (
                <img
                  src={thumbUrl(artist.imageUrl, 320) ?? undefined}
                  alt={artist.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <Mic2 className="h-8 w-8 text-white/40" />
              )}
            </span>

            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/55">
                Artista
              </p>
              <h1 className="mt-2 font-display text-3xl font-medium tracking-[-0.03em] text-white lg:text-4xl">
                {artist.name}
              </h1>

              {bio ? (
                <p className="mt-3 max-w-2xl whitespace-pre-line text-sm leading-relaxed text-white/60">
                  {shortBio}
                  {bio.length > 420 && (
                    <button
                      type="button"
                      onClick={() => setExpanded((v) => !v)}
                      className="ml-1 font-semibold text-white/80 hover:text-white"
                    >
                      {expanded ? "menos" : "mais"}
                    </button>
                  )}
                </p>
              ) : null}

              <div className="mt-6 flex flex-wrap items-center gap-2">
                {isAuthenticated ? (
                  <Button
                    onClick={() => toggle.mutate()}
                    disabled={toggle.isPending}
                    variant={following.data ? "secondary" : "default"}
                    className="rounded-full"
                  >
                    <Heart
                      className={`mr-2 h-4 w-4 ${following.data ? "fill-current" : ""}`}
                    />
                    {following.data ? "Seguindo" : "Seguir artista"}
                  </Button>
                ) : (
                  <Button asChild className="rounded-full">
                    <Link to="/auth">
                      <Heart className="mr-2 h-4 w-4" />
                      Seguir artista
                    </Link>
                  </Button>
                )}

                {links.map(({ url, label, Icon }) => (
                  <a
                    key={label}
                    href={url!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-9 items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3.5 text-xs font-semibold text-white/80 transition hover:bg-white/[0.12] hover:text-white"
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`${PAGE_CONTAINER} pb-12`}>
        {artist.hasVinyl ? (
          <>
            {/* Destaques */}
            <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <HighlightCard
                label="Mais ouvido"
                title={artist.mostPlayed?.title}
                meta={artist.mostPlayed?.year ? String(artist.mostPlayed.year) : "Segundo o Spotify"}
                cover={artist.mostPlayed?.cover ?? null}
                href={artist.mostPlayed?.url ?? null}
              />
              <HighlightCard
                label="Mais colecionado na Viny"
                title={artist.mostCollected?.title}
                meta={
                  artist.mostCollected
                    ? `${artist.mostCollected.collectorsCount} colecionador${
                        artist.mostCollected.collectorsCount === 1 ? "" : "es"
                      }`
                    : undefined
                }
                cover={artist.mostCollected?.cover ?? null}
              />
              <HighlightCard
                label="Último lançamento em vinil"
                title={artist.latestVinyl?.title}
                meta={artist.latestVinyl?.year ? String(artist.latestVinyl.year) : "Edição em vinil"}
                cover={artist.latestVinyl?.cover ?? null}
                albumParam={
                  artist.latestVinyl?.discogsId
                    ? buildAlbumParam(artist.latestVinyl.discogsId, artist.name, artist.latestVinyl.title)
                    : null
                }
              />
            </div>

            {/* Discografia */}
            <Discography artist={artist} />
          </>
        ) : artist.vinylStatus === "none" ? (
          // Aqui o foco é exclusivamente vinil: sem edição em vinil, só o aviso.
          <div className="rounded-[var(--radius-2xl)] border border-border/60 bg-surface/40 px-6 py-14 text-center">
            <Disc3 className="mx-auto h-10 w-10 text-muted-foreground/50" />
            <p className="mt-4 font-display text-lg font-medium">
              {artist.name} não tem nenhum disco em vinil
            </p>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              A Viny é só vinil. Não encontramos nenhuma edição em vinil desse artista, então não há
              discografia para mostrar por aqui.
            </p>
          </div>
        ) : (
          <div className="rounded-[var(--radius-2xl)] border border-border/60 bg-surface/40 px-6 py-14 text-center">
            <Disc3 className="mx-auto h-10 w-10 animate-pulse text-muted-foreground/50" />
            <p className="mt-4 font-display text-lg font-medium">Não foi possível verificar os vinis agora</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              A fonte está temporariamente indisponível. Isso não significa que o artista não tenha discos em vinil.
            </p>
          </div>
        )}
      </div>

    </AppShell>
  );
}

function ArtistPageLoading() {
  return (
    <AppShell>
      <div className={`${PAGE_CONTAINER} pb-12 pt-4 lg:pt-8`} aria-label="Carregando artista">
        <div className="rounded-[var(--radius-2xl)] bg-surface p-6 lg:p-10">
          <div className="flex items-center gap-6">
            <div className="h-24 w-24 shrink-0 animate-pulse rounded-full bg-muted lg:h-32 lg:w-32" />
            <div className="flex-1 space-y-3">
              <div className="h-3 w-20 animate-pulse rounded bg-muted" />
              <div className="h-9 max-w-sm animate-pulse rounded bg-muted" />
              <div className="h-4 max-w-xl animate-pulse rounded bg-muted" />
            </div>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: 12 }).map((_, index) => (
            <div key={index} className="aspect-square animate-pulse rounded-lg bg-surface" />
          ))}
        </div>
      </div>
    </AppShell>
  );
}

function Discography({ artist }: { artist: ArtistPage }) {
  const [q, setQ] = useState("");
  const [year, setYear] = useState("all");

  const years = useMemo(() => {
    const set = new Set<number>();
    for (const r of artist.discography) if (r.year) set.add(r.year);
    return [...set].sort((a, b) => b - a);
  }, [artist.discography]);

  // Agrupa por década para a lista não ficar esparsa (um disco por ano),
  // mantendo tudo do mais novo para o mais antigo.
  const groups = useMemo(() => {
    const fold = (s: string) =>
      s
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    const term = fold(q.trim());
    const filtered = artist.discography
      .filter((r) => {
        if (term && !fold(r.title).includes(term)) return false;

        if (year !== "all" && String(r.year ?? "") !== year) return false;
        return true;
      })
      .sort((a, b) => (b.year ?? 0) - (a.year ?? 0));

    const byDecade = new Map<number | null, typeof filtered>();
    for (const r of filtered) {
      const key = r.year ? Math.floor(r.year / 10) * 10 : null;
      const arr = byDecade.get(key);
      if (arr) arr.push(r);
      else byDecade.set(key, [r]);
    }
    return [...byDecade.entries()].sort((a, b) => (b[0] ?? -1) - (a[0] ?? -1));
  }, [artist.discography, q, year]);

  const total = groups.reduce((n, [, items]) => n + items.length, 0);


  return (
    <>
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold">Discografia em vinil</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {artist.discography.length} lançamento
            {artist.discography.length === 1 ? "" : "s"} com edição em vinil, do mais novo ao mais
            antigo.
          </p>
        </div>

        {artist.discography.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="relative flex-1 lg:w-56">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar disco"
                className="h-10 rounded-full pl-9"
              />
            </div>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="h-10 w-32 rounded-full">
                <SelectValue placeholder="Ano" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os anos</SelectItem>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {artist.discography.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Ainda não encontramos edições em vinil deste artista.
        </p>
      ) : total === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum disco encontrado com esse filtro.</p>
      ) : (
        <div className="space-y-8">
          {groups.map(([decade, items]) => (
            <section key={decade ?? "sem-ano"}>
              <div className="mb-3 flex items-center gap-3">
                <h3 className="font-display text-base font-semibold tabular-nums">
                  {decade === null ? "Sem ano" : `Anos ${decade}`}
                </h3>
                <span className="text-[11px] text-muted-foreground">
                  {items.length} disco{items.length === 1 ? "" : "s"}
                </span>
                <span className="h-px flex-1 bg-[color:var(--hairline)]" />
              </div>


              <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                {items.map((r) => {
                  const card = (
                    <>
                      <div className="aspect-square overflow-hidden rounded-lg bg-surface">
                        {r.coverUrl ? (
                          <img
                            src={thumbUrl(r.coverUrl, 480) ?? undefined}
                            alt={r.title}
                            className="h-full w-full object-cover transition group-hover:scale-[1.03]"
                            loading="lazy"
                          />
                        ) : (
                          <div className="grid h-full w-full place-items-center">
                            <Mic2 className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <p className="mt-2 truncate text-sm font-semibold group-hover:text-[color:var(--action)]">
                        {r.title}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {r.year ?? "—"}
                        {r.collectorsCount > 0 && (
                          <span className="ml-2 inline-flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {r.collectorsCount}
                          </span>
                        )}
                      </p>
                    </>
                  );
                  return (
                    <li key={`${r.title}-${r.year ?? "x"}`}>
                      {r.discogsId ? (
                        <Link
                          to="/album/$id"
                          params={{ id: buildAlbumParam(r.discogsId, artist.name, r.title) }}
                          className="group block"
                        >
                          {card}
                        </Link>
                      ) : (
                        <div className="group">{card}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </>
  );
}


function HighlightCard({
  label,
  title,
  meta,
  cover,
  href,
  albumParam,
}: {
  label: string;
  title?: string;
  meta?: string;
  cover: string | null;
  href?: string | null;
  albumParam?: string | null;
}) {
  const body = (
    <div className="flex items-center gap-3 rounded-2xl border border-[color:var(--hairline)] bg-surface/40 p-4 transition hover:border-[color:var(--action)]">
      <span className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-lg bg-surface-elevated">
        {cover ? (
          <img src={thumbUrl(cover, 320) ?? undefined} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
        ) : (
          <Mic2 className="h-4 w-4 text-muted-foreground" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </span>
        <span className="mt-1 block truncate text-sm font-semibold">{title ?? "Sem dados"}</span>
        {meta && <span className="block text-[11px] text-muted-foreground">{meta}</span>}
      </span>
      {href && <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
    </div>
  );

  if (albumParam) {
    return (
      <Link to="/album/$id" params={{ id: albumParam }} className="block">
        {body}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="block">
        {body}
      </a>
    );

  }
  return body;
}
